/**
 * workspaceAtoms.ts
 *
 * State atoms related to workspaces and their contents
 *
 * Primary responsibilities:
 * - Manage workspace objects and metadata
 * - Track selected workspace
 * - Handle workspace images
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { atomFamily } from 'jotai/utils'
import { Tables } from '@/supabase/types'
import { WorkspaceImage } from '@/types'
import { lazyStorage } from '@/lib/storage/lazy-storage'
import {
  patchSidebarChatSnapshots,
  prependSidebarChatSnapshots,
  removeSidebarChatSnapshots
} from '@/lib/sidebar/sidebar-chat-store'

// ===== Workspace data atoms =====

// List of all workspaces
export const workspacesAtom = atom<Tables<'workspaces'>[]>([])

// Selected workspace ID (context-synced, not persisted here)
export const selectedWorkspaceIdAtom = atom<string | null>(null)

// Selected workspace (derived)
export const selectedWorkspaceAtom = atom<Tables<'workspaces'> | null>(null)

// Workspace images
export const workspaceImagesAtom = atom<WorkspaceImage[]>([])

// Family of workspace data atoms indexed by workspace ID
export const workspaceDataFamily = atomFamily((workspaceId: string) =>
  atom<Tables<'workspaces'> | null>(null)
)

// ===== Workspace items (files, folders) =====

export interface WorkspaceItem {
  id: string
  name: string
  type: 'file' | 'folder'
  parentId: string | null
  createdAt: string
  updatedAt: string
  content?: string
  metadata?: Record<string, any>
}

// Map of workspace items by workspace ID
export const workspaceItemsAtom = atom<Record<string, WorkspaceItem[]>>({})

// Selected item in workspace
export const selectedWorkspaceItemAtom = atom<WorkspaceItem | null>(null)

// ===== Workspace UI state =====
export const isWorkspaceSidebarOpenAtom = atom(true)

export const isItemRenameModalOpenAtom = atom(false)

export const isWorkspaceSettingsOpenAtom = atom(false)

export const workspaceFilterTextAtom = atom('')

// ===== Helper functions =====

// Select workspace
export const selectWorkspace = (workspaceId: string | null): string | null => {
  return workspaceId
}

// Create new workspace - returns the new workspace object
export const createWorkspace = (
  workspace: Omit<Tables<'workspaces'>, 'id'>
): Omit<Tables<'workspaces'>, 'id'> => {
  return workspace
}

// Create workspace item - returns the item to add
export const createWorkspaceItem = (item: WorkspaceItem): WorkspaceItem => {
  return item
}

// Update workspace item - returns updated items list
export const updateWorkspaceItem = (
  itemId: string,
  updates: Partial<WorkspaceItem>,
  currentItems: WorkspaceItem[]
): WorkspaceItem[] => {
  return currentItems.map((item) => (item.id === itemId ? { ...item, ...updates } : item))
}

// Delete workspace item - returns updated items list
export const deleteWorkspaceItem = (
  itemId: string,
  currentItems: WorkspaceItem[]
): WorkspaceItem[] => {
  return currentItems.filter((item) => item.id !== itemId)
}

// Per-workspace data atoms using atom families
export interface WorkspaceData {
  files: Tables<'files'>[]
  folders: Tables<'folders'>[]
  collections: Tables<'collections'>[]
  tools: Tables<'tools'>[]
}

// Complete workspace data by workspace ID
export const workspaceDataAtom = atomFamily((workspaceId: string) =>
  atom<WorkspaceData>({
    files: [],
    folders: [],
    collections: [],
    tools: []
  })
)

// Currently selected workspace data
export const currentWorkspaceDataAtom = atom<WorkspaceData>((get) => {
  const selectedWorkspaceId = get(selectedWorkspaceIdAtom)
  if (!selectedWorkspaceId)
    return {
      files: [],
      folders: [],
      collections: [],
      tools: []
    }

  return get(workspaceDataAtom(selectedWorkspaceId))
})

// Simplified access to common data
export const currentWorkspaceFilesAtom = atom<Tables<'files'>[]>(
  (get) => get(currentWorkspaceDataAtom).files
)

export const currentWorkspaceFoldersAtom = atom<Tables<'folders'>[]>(
  (get) => get(currentWorkspaceDataAtom).folders
)

export const currentWorkspaceCollectionsAtom = atom<Tables<'collections'>[]>(
  (get) => get(currentWorkspaceDataAtom).collections
)

export const currentWorkspaceToolsAtom = atom<Tables<'tools'>[]>(
  (get) => get(currentWorkspaceDataAtom).tools
)

// ===== Workspace UI state atoms =====
export const isCreatingWorkspaceAtom = atom(false)
export const isEditingWorkspaceAtom = atom(false)

export const workspaceFormDataAtom = atom<{
  name: string
  description: string
  instructions: string
  imageUrl: string | null
}>({
  name: '',
  description: '',
  instructions: '',
  imageUrl: null
})

// ===== Helper functions =====

type WorkspaceIdGetter = {
  selectedWorkspaceIdAtom: ReturnType<typeof atom<string | null>>
}

export const setSelectedWorkspaceId =
  (workspaceId: string | null) => (getters: WorkspaceIdGetter) => {
    return {
      selectedWorkspaceIdAtom: workspaceId
    }
  }

type WorkspaceDataGetter = {
  workspaceDataAtom: any // Fix the complex type
}

export const setWorkspaceData =
  (workspaceId: string, data: Partial<WorkspaceData>) => (getters: WorkspaceDataGetter) => {
    // Implementation will be updated in hook
    return { workspaceId, data }
  }

type WorkspacesGetter = {
  workspacesAtom: ReturnType<typeof atom<Tables<'workspaces'>[]>>
}

// This function replaces the first updateWorkspace implementation
export const updateWorkspaceInList =
  (workspace: Tables<'workspaces'>) => (getters: WorkspacesGetter) => {
    return {
      workspacesAtom: (prev: Tables<'workspaces'>[]) =>
        prev.map((w) => (w.id === workspace.id ? workspace : w))
    }
  }

type WorkspaceManagerGetters = {
  workspacesAtom: ReturnType<typeof atom<Tables<'workspaces'>[]>>
  selectedWorkspaceIdAtom: ReturnType<typeof atom<string | null>>
}

// This function replaces the first deleteWorkspace implementation
export const removeWorkspaceFromList =
  (workspaceId: string) => (getters: WorkspaceManagerGetters) => {
    return {
      workspacesAtom: (prev: Tables<'workspaces'>[]) =>
        prev.filter((workspace) => workspace.id !== workspaceId),
      selectedWorkspaceIdAtom: (prev: string | null) => (prev === workspaceId ? null : prev)
    }
  }

// Workspace image lookup function
export const workspaceImageByIdAtom = atom((get) => {
  const workspaceImages = get(workspaceImagesAtom)
  return (workspaceId: string) =>
    workspaceImages.find((image) => image.workspaceId === workspaceId) || null
})

// Workspace items atoms
export const chatsAtom = atom<(Tables<'chats'> & { space_icon?: string | null })[]>([])
export const filesAtom = atom<Tables<'files'>[]>([])
export const foldersAtom = atom<Tables<'folders'>[]>([])
export const promptsAtom = atom<Tables<'prompts'>[]>([])
export const presetsAtom = atom<Tables<'presets'>[]>([])

// Selected item atoms
export const selectedFolderIdAtom = atomWithStorage<string | null>('selectedFolderId', null)
export const selectedPresetAtom = atom<Tables<'presets'> | null>(null)

// Helper functions

// Get workspace by ID
export const getWorkspaceById = (workspaceId: string, workspaces: Tables<'workspaces'>[]) => {
  return workspaces.find((workspace) => workspace.id === workspaceId) || null
}

// Get workspace name by ID
export const getWorkspaceName = (workspaceId: string, workspaces: Tables<'workspaces'>[]) => {
  const workspace = workspaces.find((w) => w.id === workspaceId)
  return workspace?.name || 'Unknown Workspace'
}

// Get workspace image URL by ID
export const getWorkspaceImageUrl = (workspaceId: string, workspaceImages: WorkspaceImage[]) => {
  const workspaceImage = workspaceImages.find((image) => image.workspaceId === workspaceId)
  return workspaceImage?.url || ''
}

type AddChatAtomPayload =
  | Tables<'chats'>
  | {
      chat: Tables<'chats'>
      viewerUserId?: string | null
    }

// Optimized helper functions for atomic chat updates
export const addChatAtom = atom(null, (get, set, payload: AddChatAtomPayload) => {
  const newChat = 'chat' in payload ? payload.chat : payload
  const viewerUserId = 'chat' in payload ? payload.viewerUserId : null
  const chats = get(chatsAtom)

  // Avoid unnecessary updates by checking if chat already exists
  if (chats.some((chat) => chat.id === newChat.id)) {
    // If chat exists, use updateChatAtom instead
    const existingChat = chats.find((chat) => chat.id === newChat.id)
    if (JSON.stringify(existingChat) !== JSON.stringify(newChat)) {
      set(
        chatsAtom,
        chats.map((chat) => (chat.id === newChat.id ? newChat : chat))
      )
      patchSidebarChatSnapshots(newChat.id, () => newChat)
    }
    return
  }

  // Add the new chat to the beginning of the list (most recent first)
  set(chatsAtom, [newChat, ...chats])
  prependSidebarChatSnapshots(newChat, {
    userId: viewerUserId ?? newChat.user_id
  })
})

export const updateChatAtom = atom(null, (get, set, updatedChat: Tables<'chats'>) => {
  const chats = get(chatsAtom)

  // Check if the chat exists
  const chatExists = chats.some((chat) => chat.id === updatedChat.id)
  if (!chatExists) {
    // If chat doesn't exist, add it instead
    set(chatsAtom, [updatedChat, ...chats])
    return
  }

  // Only update if there's an actual change to avoid unnecessary renders
  const existingChat = chats.find((chat) => chat.id === updatedChat.id)
  if (JSON.stringify(existingChat) === JSON.stringify(updatedChat)) {
    return // No change, skip update
  }

  // Replace the updated chat in the list
  set(
    chatsAtom,
    chats.map((chat) => (chat.id === updatedChat.id ? updatedChat : chat))
  )
  patchSidebarChatSnapshots(updatedChat.id, () => updatedChat)
})

export const removeChatAtom = atom(null, (get, set, chatId: string) => {
  const chats = get(chatsAtom)

  // Check if chat exists before updating state
  if (!chats.some((chat) => chat.id === chatId)) {
    return // Chat doesn't exist, no update needed
  }

  // Remove the chat from the list
  set(
    chatsAtom,
    chats.filter((chat) => chat.id !== chatId)
  )
  removeSidebarChatSnapshots(chatId)
})
